import { useState, useEffect } from 'react';

// Material UI
import { styled, useTheme } from '@mui/material/styles';
import {
    Box,
    CircularProgress,
    InputAdornment,
    FormControlLabel,
    Checkbox,
    Button,
    Grid,
    FormHelperText,
} from '@mui/material';
import { Table, TableBody, TableRow, TableCell } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Card, CardContent, CardHeader } from '@mui/material';
import { Radio, RadioGroup } from '@mui/material';

// Components
import HostPricingDetails from './HostPricingDetails';
import ConfirmAction from 'components/popups/ConfirmAction';
import AddressAutocomplete from 'components/ui/AddressAutocomplete';
import { Dropdown, TextField, TextArea, ProvinceSelector } from 'components/ui/inputs';
import { Section, Header, Content } from 'components/ui/forms';
import { DeleteButton } from './ui/buttons';
import { SubmitButton } from './ui/buttons';
import { CancelButton } from './ui/buttons';

// Utility
import { useNotification } from 'context/notification';
import fetchExpresso from 'utility/fetchExpresso';
import { validateURL, validatePostalCode } from 'utility/validation';
import { useRadio, useTextInput, useToggle, useNumberInput } from 'hooks/forms';
import { parseDecimalToPercent, parsePercentToDecimal } from 'utility/numbers';

export default function HostFormContainer(props) {
    const [state, setState] = useState({
        loading: true,
        hostTypes: null,
        merchantTypes: null,
        paymentProcessors: null,
    });

    useEffect(() => {
        async function getData() {
            try {
                const results = await Promise.all([
                    fetchExpresso('/apiv2/host_types'),
                    fetchExpresso(`/apiv2/merch_types`),
                    fetchExpresso(`/apiv2/payment_processors`),
                ]);

                const { hostTypes } = await results[0].json();
                const { merchantTypes } = await results[1].json();
                const { paymentProcessors } = await results[2].json();

                setState({ loading: false, hostTypes, merchantTypes, paymentProcessors });
            } catch (error) {
                setState({
                    loading: false,
                    hostTypes: null,
                    merchantTypes: null,
                    paymentProcessors: null,
                });
            }
        }
        getData();
    }, []);

    if (state.loading) {
        return (
            <Box
                display="flex"
                flex={1}
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
            >
                <CircularProgress />
            </Box>
        );
    }

    if (!state.hostTypes || !state.merchantTypes || !state.paymentProcessors) {
        return (
            <Box
                display="flex"
                flex={1}
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
            >
                <p>Error loading host/merchant types</p>
            </Box>
        );
    }

    if (props.hostId) {
        return (
            <HostEditForm
                {...props}
                hostTypes={state.hostTypes}
                merchantTypes={state.merchantTypes}
                paymentProcessors={state.paymentProcessors}
                editMode={true}
            />
        );
    } else {
        return (
            <HostCreateForm
                {...props}
                hostTypes={state.hostTypes}
                merchantTypes={state.merchantTypes}
                paymentProcessors={state.paymentProcessors}
                editMode={false}
            />
        );
    }
}

function HostCreateForm(props) {
    const { onUpdate, onCancel, hostTypes, merchantTypes, paymentProcessors } = props;

    const [loading, setLoading] = useState(false);
    const { notify } = useNotification();

    const handleSubmit = (form) => {
        setLoading(true);
        if (form.connectType === 'legacy') {
            form.connectType = null;
            form.connectEnabled = 0;
        } else {
            form.connectEnabled = 1;
        }

        if (form.ccFeeOrderPct) form.ccFeeOrderPct = parsePercentToDecimal(form.ccFeeOrderPct, 4);

        fetchExpresso(`/apiv2/hosts/`, { method: 'POST', body: form })
            .then((res) => {
                if (res.status === 200) {
                    notify.success('Created new host');
                    onUpdate();
                } else {
                    throw new Error();
                }
            })
            .catch(() => notify.error('There was a problem creating your host'))
            .finally(() => setLoading(false));
    };

    return (
        <HostForm
            editMode={false}
            hostTypes={hostTypes}
            merchantTypes={merchantTypes}
            paymentProcessors={paymentProcessors}
            paymentProcessor={paymentProcessors[0]?.payment_processor_id}
            ccFeeOrderPct={parseDecimalToPercent(paymentProcessors[0]?.cc_fee_order_pct, 4)}
            ccFeeOrderAmt={Number(paymentProcessors[0]?.cc_fee_order_amt)}
            loading={loading}
            onSubmit={handleSubmit}
            onCancel={onCancel}
        />
    );
}

function HostEditForm(props) {
    const { onUpdate, onCancel, hostId, hostTypes, merchantTypes, paymentProcessors } = props;

    const [host, setHost] = useState(null);
    const [loadingHost, setLoadingHost] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        Promise.all([
            fetchExpresso(`/apiv2/hosts/${hostId}`).then((res) => res.json()),
            fetchExpresso(`/apiv2/hosts/${hostId}/host_pricing`).then((res) => res.json()),
        ])
            .then(([host, hostPricing]) => {
                setHost({
                    ...host,
                    cc_fee_order_amt: hostPricing.cc_fee_order_amt,
                    cc_fee_order_pct: hostPricing.cc_fee_order_pct,
                });
                setLoadingHost(false);
            })
            .catch((error) => {
                setError(error);
                setLoadingHost(false);
            });
    }, [hostId]);

    const [loading, setLoading] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleteResults, setDeleteResults] = useState(null);

    const { notify } = useNotification();

    const handleSubmit = async (form) => {
        setLoading(true);

        if (form.connectType === 'legacy') {
            form.connectType = null;
            form.connectEnabled = 0;
        } else {
            form.connectEnabled = 1;
        }

        if (form.ccFeeOrderPct) form.ccFeeOrderPct = parsePercentToDecimal(form.ccFeeOrderPct, 4);

        fetchExpresso(`/apiv2/hosts/${hostId}`, { method: 'PUT', body: form })
            .then((res) => {
                if (res.status === 200) {
                    notify.success('Host updated');
                    onUpdate();
                } else {
                    throw new Error();
                }
            })
            .catch(() => notify.error('There was a problem updating your host'))
            .finally(() => setLoading(false));
    };

    const handleDelete = () => {
        setConfirmDelete(false);
        setLoading(true);

        // Delete Host
        fetchExpresso(`/apiv2/hosts/${props.hostId}`, { method: 'DELETE' })
            .then((res) => {
                if (res.status !== 200) throw new Error();
                return res.json();
            })
            .then((data) => setDeleteResults(Object.entries(data)))
            .catch(() => notify.error('There was a problem deleting your host'))
            .finally(() => setLoading(false));
    };

    if (loadingHost) {
        return (
            <Box
                display="flex"
                flex={1}
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                display="flex"
                flex={1}
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
            >
                <p>Error loading host</p>
            </Box>
        );
    }

    if (host) {
        const {
            url_website,
            url_facebook,
            url_twitter,
            url_instagram,
            url_tiktok,
            url_linkedin,
            url_youtube,
            connect_type_id,
            robin_enabled_flag,
            seats_enabled_flag,
            seats_public_key,
            seats_private_key,
            address1,
            address2,
            city,
            prov_state,
            postal_code,
            country,
        } = host;

        let connectType = 'legacy';
        if (connect_type_id === 1) connectType = 'custom';
        if (connect_type_id === 2) connectType = 'express';
        if (connect_type_id === 3) connectType = 'standard';

        return (
            <>
                <HostForm
                    hostId={hostId}
                    loading={loading}
                    editMode={true}
                    hostTypes={hostTypes}
                    merchantTypes={merchantTypes}
                    paymentProcessors={paymentProcessors}
                    onSubmit={handleSubmit}
                    onDelete={handleDelete}
                    onCancel={onCancel}
                    name={host.host_name}
                    type={host.host_type_id}
                    merchType={host.merch_type_id}
                    paymentProcessor={host.payment_processor_id}
                    ccFeeOrderPct={parseDecimalToPercent(host.cc_fee_order_pct, 4)}
                    ccFeeOrderAmt={Number(host.cc_fee_order_amt)}
                    description={host.host_desc}
                    ccStatementDescriptor={host.cc_statement_descriptor}
                    address1={address1}
                    address2={address2}
                    city={city}
                    provState={prov_state}
                    postalCode={postal_code}
                    country={country}
                    website={url_website}
                    facebook={url_facebook}
                    twitter={url_twitter}
                    instagram={url_instagram}
                    tiktok={url_tiktok}
                    linkedin={url_linkedin}
                    youtube={url_youtube}
                    connectType={connectType}
                    robinEnabled={robin_enabled_flag}
                    seatsIoEnabled={seats_enabled_flag}
                    seatsIoPrivateKey={seats_private_key}
                    seatsIoPublicKey={seats_public_key}
                />
                <ConfirmAction
                    open={confirmDelete}
                    destructive
                    onCancel={() => setConfirmDelete(false)}
                    onConfirm={handleDelete}
                    confirmText="Delete"
                    title="Delete this host?"
                    description="This will delete all data associated with this host, including events, tickets, and purchase records"
                />

                {deleteResults !== null && (
                    <Dialog open onClose={onUpdate}>
                        <DialogTitle>Results</DialogTitle>
                        <DialogContent>
                            <Table>
                                <TableBody>
                                    {deleteResults.map((p) => (
                                        <TableRow key={p[0]}>
                                            <TableCell>{p[0]}</TableCell>
                                            <TableCell>{p[1]}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </DialogContent>
                        <DialogActions>
                            <Button color="secondary" onClick={onUpdate}>
                                OK
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
            </>
        );
    }
}

function HostForm(props) {
    // Props
    const {
        onSubmit,
        onCancel,
        onDelete,
        hostId,
        editMode,
        hostTypes,
        merchantTypes,
        paymentProcessors,
        loading,
    } = props;

    // About
    const name = useTextInput({ defaultValue: props.name || '', maxLength: 100 });
    const description = useTextInput({ defaultValue: props.description || '', maxLength: 1000 });
    // Financial
    const type = useNumberInput({ defaultValue: props.type });
    const merchType = useNumberInput({ defaultValue: props.merchType });
    const ccDescriptor = useTextInput({
        defaultValue: props.ccStatementDescriptor || '',
        maxLength: 18,
        noSpaces: true,
    });
    const connectType = useRadio({
        defaultValue: props.connectType || '',
        disabled: editMode, // We may want to allow changing this in the future
    });
    const paymentProcessor = useNumberInput({ defaultValue: props.paymentProcessor || '' });
    const ccFeeOrderAmt = useNumberInput({
        defaultValue: props.ccFeeOrderAmt || 0,
        decimalPlaces: 2,
    });
    const ccFeeOrderPct = useNumberInput({
        defaultValue: props.ccFeeOrderPct || 0,
        decimalPlaces: 2,
    });

    // Location
    const address1 = useTextInput({ defaultValue: props.address1 || '', maxLength: 100 });
    const address2 = useTextInput({ defaultValue: props.address2 || '', maxLength: 100 });
    const city = useTextInput({ defaultValue: props.city || '', maxLength: 100 });
    const country = useTextInput({ defaultValue: props.country || '', maxLength: 50 });
    const provState = useTextInput({ defaultValue: props.provState || '', maxLength: 100 });
    const postalCode = useTextInput({ defaultValue: props.postalCode || '' });
    const [confirmMissingAddress, setConfirmMissingAddress] = useState(false);

    // Social
    const website = useTextInput({ defaultValue: props.website || '', maxLength: 200 });
    const facebook = useTextInput({ defaultValue: props.facebook || '', maxLength: 200 });
    const twitter = useTextInput({ defaultValue: props.twitter || '', maxLength: 200 });
    const instagram = useTextInput({ defaultValue: props.instagram || '', maxLength: 200 });
    const tiktok = useTextInput({ defaultValue: props.tiktok || '', maxLength: 200 });
    const linkedin = useTextInput({ defaultValue: props.linkedin || '', maxLength: 200 });
    const youtube = useTextInput({ defaultValue: props.youtube || '', maxLength: 200 });
    const robinEnabled = useToggle({
        defaultValue: !!props.robinEnabled,
    });
    const seatsIoEnabled = useToggle({
        defaultValue: !!props.seatsIoEnabled,
    });
    const seatsIoPrivateKey = useTextInput({ defaultValue: props.seatsIoPrivateKey || '' });
    const seatsIoPublicKey = useTextInput({ defaultValue: props.seatsIoPublicKey || '' });

    const theme = useTheme();

    function handleAddressAutocomplete({
        address,
        city: selectedCity,
        province,
        countryCode: selectedCountry,
        postalCode: selectedPostalCode,
    }) {
        address1.setValue(address);
        city.setValue(selectedCity);
        country.setValue(selectedCountry);
        provState.setValue(province);
        postalCode.setValue(selectedPostalCode);
    }

    function handleCountryChange(e) {
        const selectedCountry = e.target.value;
        country.setValue(selectedCountry);
        provState.setValue('');
        postalCode.setValue('');
    }

    function handlePostalCodeChange(e) {
        const value = e.target.value;

        if (country.value === 'US') {
            postalCode.setValue(value.replace(/[^0-9]/g, '').slice(0, 9));
        } else {
            postalCode.setValue(
                value
                    .replace(/[^0-9a-zA-Z]/g, '')
                    .slice(0, 6)
                    .toUpperCase(),
            );
        }
    }

    useEffect(() => {
        // Reset type and merch type when they are not applicable
        if (connectType.value !== 'express' && connectType.value !== 'custom') {
            type.setValue('');
            merchType.setValue('');
        }

        if (connectType.value !== 'standard') {
            paymentProcessor.setValue(props.paymentProcessor || '');
            ccFeeOrderAmt.setValue(props.ccFeeOrderAmt || 0);
            ccFeeOrderPct.setValue(props.ccFeeOrderPct || 0);
        }
        // eslint-disable-next-line
    }, [connectType.value]);

    function handleSubmit(allowMissingAddress = false) {
        if (loading) return null;

        // ***** Error Handling ***** //

        // All values except name can be blank
        // Only validate if a value is provided

        let formHasError = false;

        if (name.value.trim() === '') {
            name.setError('This field cannot be empty');
            formHasError = true;
        }

        if (!connectType.value) {
            connectType.setError('This field is required');
            formHasError = true;
        }

        if (
            (connectType.value === 'express' || connectType.value === 'custom') &&
            type.value.trim() === ''
        ) {
            type.setError('This field is required');
            formHasError = true;
        }

        if (
            (connectType.value === 'express' || connectType.value === 'custom') &&
            merchType.value.trim() === ''
        ) {
            merchType.setError('This field is required');
            formHasError = true;
        }

        if (connectType.value === 'standard' && !paymentProcessor.value) {
            paymentProcessor.setError('This field is required');
            formHasError = true;
        }

        if (connectType.value === 'standard') {
            if (ccFeeOrderPct.value.trim() === '') {
                ccFeeOrderPct.setError('This field is required');
                formHasError = true;
            }

            if (ccFeeOrderAmt.value.trim() === '') {
                ccFeeOrderAmt.setError('This field is required');
                formHasError = true;
            }
        }

        if (validateURL(website.value) === false && website.value.trim() !== '') {
            website.setError(
                'Make sure your link is properly formatted. Example: https://example.com',
            );
            formHasError = true;
        }

        if (validateURL(facebook.value) === false && facebook.value.trim() !== '') {
            facebook.setError(
                'Make sure your link is properly formatted. Example: https://facebook.com/example',
            );
            formHasError = true;
        }

        if (validateURL(twitter.value) === false && twitter.value.trim() !== '') {
            twitter.setError(
                'Make sure your link is properly formatted. Example: https://x.com/example',
            );
            formHasError = true;
        }

        if (validateURL(instagram.value) === false && instagram.value.trim() !== '') {
            instagram.setError(
                'Make sure your link is properly formatted. Example: https://instagram.com/example',
            );
            formHasError = true;
        }

        if (validateURL(tiktok.value) === false && tiktok.value.trim() !== '') {
            tiktok.setError(
                'Make sure your link is properly formatted. Example: https://www.tiktok.com/@example',
            );
            formHasError = true;
        }

        if (validateURL(linkedin.value) === false && linkedin.value.trim() !== '') {
            linkedin.setError(
                'Make sure your link is properly formatted. Example: https://www.linkedin.com/company/example',
            );
            formHasError = true;
        }

        if (validateURL(youtube.value) === false && youtube.value.trim() !== '') {
            youtube.setError(
                'Make sure your link is properly formatted. Example: https://www.youtube.com/channel/example',
            );
            formHasError = true;
        }

        if (
            postalCode.value.trim() !== '' &&
            country.value.trim() !== '' &&
            !validatePostalCode(postalCode.value, country.value)
        ) {
            postalCode.setError(
                country.value === 'US'
                    ? 'Invalid ZIP code format. Example: 12345'
                    : 'Invalid postal code format. Example: A1A 1A1',
            );
            formHasError = true;
        }

        if (formHasError) {
            return null;
        }

        if (
            connectType.value === 'standard' &&
            allowMissingAddress !== true &&
            [address1, city, country, provState, postalCode].some(
                (field) => field.value.trim() === '',
            )
        ) {
            setConfirmMissingAddress(true);
            return null;
        }

        onSubmit({
            name: name.value.trim(),
            description: description.value.trim() || null,
            type: Number(type.value) || null,
            merchType: Number(merchType.value) || null,
            ccStatementDescriptor: ccDescriptor.value.trim() || null,
            connectType: connectType.value,
            paymentProcessor:
                connectType.value === 'standard' ? Number(paymentProcessor.value) : null,
            ccFeeOrderPct: connectType.value === 'standard' ? Number(ccFeeOrderPct.value) : null,
            ccFeeOrderAmt: connectType.value === 'standard' ? Number(ccFeeOrderAmt.value) : null,
            address1: address1.value.trim() || null,
            address2: address2.value.trim() || null,
            city: city.value.trim() || null,
            country: country.value.trim() || null,
            provState: provState.value.trim() || null,
            postalCode: postalCode.value.trim() || null,
            website: website.value.trim() || null,
            facebook: facebook.value.trim() || null,
            twitter: twitter.value.trim() || null,
            instagram: instagram.value.trim() || null,
            tiktok: tiktok.value.trim() || null,
            linkedin: linkedin.value.trim() || null,
            youtube: youtube.value.trim() || null,
            robinEnabled: robinEnabled.checked ? 1 : 0,
            seatsIoPublicKey: seatsIoPublicKey.value.trim() || null,
            seatsIoPrivateKey: seatsIoPrivateKey.value.trim() || null,
            seatsIoEnabled: seatsIoEnabled.checked ? 1 : 0,
        });
    }

    return (
        <Box width={1} display="flex" flexDirection="column" alignItems="center">
            <ConfirmAction
                open={confirmMissingAddress}
                onCancel={() => setConfirmMissingAddress(false)}
                onConfirm={() => {
                    setConfirmMissingAddress(false);
                    handleSubmit(true);
                }}
                title="Missing Address Details"
                description="A complete address is needed to accept POS payments with a standard Stripe account. Click Cancel to add the missing details, or Save anyway to continue."
                confirmText="Save anyway"
            />
            <Box
                width={[0.9, 0.9, theme.breakpoints.values.md - 60]}
                my={6}
                display="flex"
                justifyContent={editMode ? 'space-between' : 'center'}
                flexWrap="wrap"
            >
                <Box width={[1, 1, 0.65]} display="flex" flexDirection="column">
                    <Grid container spacing={4}>
                        <Grid item xs={12}>
                            <Section>
                                <Header>About</Header>
                                <Content>
                                    <TextField
                                        required
                                        {...name.formProps}
                                        label="Host Name"
                                        margin="normal"
                                    />
                                    <TextArea
                                        {...description.formProps}
                                        label="Description"
                                        margin="normal"
                                    />
                                </Content>
                            </Section>
                        </Grid>
                        <Grid item xs={12}>
                            <Section>
                                <Header>Financial</Header>
                                <Content>
                                    <Box mt={2} mb={2}>
                                        <RadioGroup aria-label="connect" {...connectType.formProps}>
                                            <FormControlLabel
                                                label="FrontDoor+"
                                                value="custom"
                                                control={
                                                    <Radio
                                                        disabled={connectType.formProps.disabled}
                                                    />
                                                }
                                            />
                                            {editMode === true &&
                                                connectType.value === 'legacy' && (
                                                    <FormControlLabel
                                                        label="FrontDoor+ (Legacy)"
                                                        value="legacy"
                                                        control={
                                                            <Radio
                                                                disabled={
                                                                    connectType.formProps.disabled
                                                                }
                                                            />
                                                        }
                                                    />
                                                )}
                                            {editMode === true &&
                                                connectType.value === 'express' && (
                                                    <FormControlLabel
                                                        label="FrontDoor+ (Express)"
                                                        value="express"
                                                        control={
                                                            <Radio
                                                                disabled={
                                                                    connectType.formProps.disabled
                                                                }
                                                            />
                                                        }
                                                    />
                                                )}
                                            <FormControlLabel
                                                label="Other Provider"
                                                value="standard"
                                                control={
                                                    <Radio
                                                        disabled={connectType.formProps.disabled}
                                                    />
                                                }
                                            />
                                        </RadioGroup>
                                        <FormHelperText error={connectType.formProps.error}>
                                            {connectType.formProps.helperText || ''}
                                        </FormHelperText>
                                    </Box>

                                    {(connectType.value === 'custom' ||
                                        connectType.value === 'express') && (
                                        <Box mb={2}>
                                            <Dropdown
                                                {...type.formProps}
                                                label="Business Structure"
                                                margin="normal"
                                            >
                                                <option value="" disabled></option>
                                                {hostTypes.map((type) => (
                                                    <option
                                                        key={type.host_type_id}
                                                        value={type.host_type_id}
                                                    >
                                                        {type.type_name}
                                                    </option>
                                                ))}
                                            </Dropdown>
                                            <Dropdown
                                                {...merchType.formProps}
                                                label="Merchant Type"
                                                margin="normal"
                                            >
                                                <option value="" disabled></option>
                                                {merchantTypes.map((type) => (
                                                    <option
                                                        key={type.merch_type_id}
                                                        value={type.merch_type_id}
                                                    >
                                                        {type.type_name}
                                                    </option>
                                                ))}
                                            </Dropdown>
                                        </Box>
                                    )}
                                    {connectType.value === 'standard' && (
                                        <Box mb={2}>
                                            <Dropdown
                                                {...paymentProcessor.formProps}
                                                label="Payment Processor"
                                                disabled={editMode}
                                            >
                                                <option value="" disabled></option>
                                                {paymentProcessors.map((processor) => (
                                                    <option
                                                        key={processor.payment_processor_id}
                                                        value={String(
                                                            processor.payment_processor_id,
                                                        )}
                                                    >
                                                        {processor.payment_processor_name}
                                                    </option>
                                                ))}
                                            </Dropdown>
                                            <Box display="flex" alignItems="center" mt={2} mb={4}>
                                                <TextField
                                                    {...ccFeeOrderPct.formProps}
                                                    label="Credit Card Fee (%)"
                                                    disabled={editMode}
                                                    sx={{ mr: 1 }}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                %
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                                <TextField
                                                    {...ccFeeOrderAmt.formProps}
                                                    label="Credit Card Fee (Fixed)"
                                                    disabled={editMode}
                                                    sx={{ ml: 1 }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                $
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                    <TextField
                                        {...ccDescriptor.formProps}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    FD*
                                                </InputAdornment>
                                            ),
                                        }}
                                        label="CC Statement Descriptor"
                                        margin="normal"
                                    />
                                </Content>
                            </Section>
                        </Grid>
                        <Grid item xs={12}>
                            <Section>
                                <Header>Location</Header>
                                <Content>
                                    <AddressAutocomplete
                                        defaultValue={address1.formProps.value}
                                        error={address1.formProps.error}
                                        helperText={address1.formProps.helperText}
                                        onChange={address1.formProps.onChange}
                                        onPlaceSelect={handleAddressAutocomplete}
                                        label="Address Line 1"
                                        margin="normal"
                                        country={country.value}
                                    />
                                    <TextField
                                        {...address2.formProps}
                                        label="Address Line 2"
                                        margin="normal"
                                    />
                                    <TextField {...city.formProps} label="City" margin="normal" />
                                    <Dropdown
                                        {...country.formProps}
                                        label="Country"
                                        margin="normal"
                                        onChange={handleCountryChange}
                                    >
                                        <option value="" disabled></option>
                                        <option value="CA">Canada</option>
                                        <option value="US">United States</option>
                                    </Dropdown>
                                    <ProvinceSelector
                                        {...provState.formProps}
                                        region={country.value}
                                        margin="normal"
                                    />
                                    <TextField
                                        {...postalCode.formProps}
                                        onChange={handlePostalCodeChange}
                                        label={country.value === 'US' ? 'ZIP' : 'Postal Code'}
                                        placeholder={country.value === 'US' ? '12345' : 'A1A 1A1'}
                                        margin="normal"
                                    />
                                </Content>
                            </Section>
                        </Grid>
                        <Grid item xs={12}>
                            <Section>
                                <Header>Socials</Header>
                                <Content>
                                    <TextField
                                        {...website.formProps}
                                        type="url"
                                        label="Website URL"
                                        placeholder="https://example.com"
                                        margin="normal"
                                    />

                                    <TextField
                                        {...facebook.formProps}
                                        type="url"
                                        label="Facebook URL"
                                        placeholder="https://facebook.com/example"
                                        margin="normal"
                                    />

                                    <TextField
                                        {...instagram.formProps}
                                        type="url"
                                        label="Instagram URL"
                                        placeholder="https://instagram.com/example"
                                        margin="normal"
                                    />

                                    <TextField
                                        {...twitter.formProps}
                                        type="url"
                                        label="X URL"
                                        placeholder="https://x.com/example"
                                        margin="normal"
                                    />

                                    <TextField
                                        {...tiktok.formProps}
                                        type="url"
                                        label="Tiktok URL"
                                        placeholder="https://www.tiktok.com/@example"
                                        margin="normal"
                                    />

                                    <TextField
                                        {...linkedin.formProps}
                                        type="url"
                                        label="LinkedIn URL"
                                        placeholder="https://www.linkedin.com/company/example"
                                        margin="normal"
                                    />

                                    <TextField
                                        {...youtube.formProps}
                                        type="url"
                                        label="YouTube URL"
                                        placeholder="https://www.youtube.com/channel/example"
                                        margin="normal"
                                    />
                                </Content>
                            </Section>
                        </Grid>

                        <Grid item xs={12}>
                            <Section>
                                <Header>Integrations</Header>
                                <Content>
                                    <FormControlLabel
                                        control={
                                            <Checkbox {...robinEnabled.formProps} color="primary" />
                                        }
                                        label="Enable Robin"
                                    />
                                </Content>
                            </Section>
                        </Grid>

                        <Grid item xs={12}>
                            <Section>
                                <Header>Seats.io Integration</Header>
                                <Content>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                {...seatsIoEnabled.formProps}
                                                color="primary"
                                            />
                                        }
                                        label="Enable Seats.io"
                                    />

                                    {seatsIoEnabled.checked &&
                                        editMode &&
                                        seatsIoPublicKey.value &&
                                        seatsIoPrivateKey.value && (
                                            <>
                                                <TextField
                                                    {...seatsIoPublicKey.formProps}
                                                    label="Workspace Public Key"
                                                    placeholder=""
                                                    margin="normal"
                                                    disabled
                                                />

                                                <TextField
                                                    {...seatsIoPrivateKey.formProps}
                                                    label="Workspace Private Key"
                                                    placeholder=""
                                                    margin="normal"
                                                    disabled
                                                />
                                            </>
                                        )}
                                </Content>
                            </Section>
                        </Grid>
                    </Grid>

                    <ButtonContainer>
                        {editMode && process.env.REACT_APP_ENV === 'development' && (
                            <DeleteButton
                                sx={{ marginRight: 'auto' }}
                                onClick={onDelete}
                                disabled={loading}
                            >
                                Delete
                            </DeleteButton>
                        )}
                        <CancelButton onClick={onCancel} disabled={loading}>
                            Cancel
                        </CancelButton>
                        <SubmitButton onClick={handleSubmit} disabled={loading}>
                            Save
                        </SubmitButton>
                    </ButtonContainer>
                </Box>

                {editMode && (
                    <Box width={[1, 1, 0.3]}>
                        <Card>
                            <CardHeader title="Fee Breakdown" />
                            <CardContent>
                                <HostPricingDetails hostId={hostId} />
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

const ButtonContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
}));
